import type { FlowReopenState } from "@repo/shared/types";
import { RotateCcw } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./reopen-flow-dialog.i18n";

const MAX_REASON = 500;

type ReopenFlowDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What reopening would remove, and by which path — from the process state. */
  reopen: FlowReopenState;
  /** True while the request is in flight: blocks a second confirm. */
  isPending?: boolean;
  /** Set when the last attempt failed, so the dialog can offer a retry. */
  errorMessage?: string | null;
  onConfirm: (reason: string | null) => void;
};

/**
 * Confirmation for returning a flow to editing, from the flow header and from
 * the process page.
 *
 * Only the consequences that actually apply are listed — an advisor reading
 * "2 generated PDFs will be deleted" on a flow with no PDFs learns to skip the
 * list. A cancelled flow never entered the process, so it gets the shorter
 * confirmation with no consequences and no reason field.
 *
 * Signature cancellation is worded as requested rather than done, because that
 * is what it is: the provider is asked, and the flow reopens either way once
 * we have verified nothing was signed.
 */
export function ReopenFlowDialog({
  open,
  onOpenChange,
  reopen,
  isPending = false,
  errorMessage = null,
  onConfirm,
}: ReopenFlowDialogProps) {
  const t = useT(T);
  const [reason, setReason] = useState("");

  const { cleanup, mode } = reopen;
  const isCancelledFlow = mode === "reopen";

  const items: string[] = [];
  if (cleanup.activeSignatureCount > 0) {
    items.push(t.signature, t.signatureCancel);
  }
  if (cleanup.pdfCount > 0) {
    const line = cleanup.pdfCount === 1 ? t.pdfs : t.pdfsPlural;
    items.push(line(cleanup.pdfCount));
  }
  if (cleanup.hasPendingExternal) items.push(t.external);
  if (cleanup.hasPendingCompliance) items.push(t.compliance);
  if (cleanup.draftOrderCount > 0) items.push(t.draftOrders);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isPending) return;
        if (!next) setReason("");
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>
            {isCancelledFlow ? t.descriptionCancelled : t.description}
          </DialogDescription>
        </DialogHeader>

        {items.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t.forThisFlow}</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-sm text-muted-foreground">{t.historyNote}</p>

        {/* A cancelled flow gets the shorter confirmation, without this. */}
        {!isCancelledFlow && (
          <div className="space-y-2">
            <Label htmlFor="reopen-flow-reason">{t.reasonLabel}</Label>
            <Textarea
              id="reopen-flow-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t.reasonPlaceholder}
              maxLength={MAX_REASON}
              rows={3}
            />
            <p className="text-right text-xs text-muted-foreground">
              {reason.length} / {MAX_REASON}
            </p>
          </div>
        )}

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        <DialogFooter>
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
          {/* QL-223 — the label stays put while the action runs; the spinner
              and the disabled state are what say it is working, and they also
              stop a second confirm. */}
          <Button
            loading={isPending}
            onClick={() => onConfirm(reason.trim() || null)}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            {t.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
